import ArgChip from '@/components/common/chips/ArgChip';
import VariablePicker from '@/components/nodes/settings/variable/VariablePicker';
import ErrorNodeWrapper from '@/components/validation/ErrorNodeWrapper';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { Arg } from '@/types/subroutine';
import { ValidationError } from '@/types/validation';
import { VariableRef } from '@/types/variable';
import { variableExists } from '@/util/validation/validation';
import { getChipType, getVariableScope } from '@/util/variables';
import { FC, useState } from 'react';

type ArgViewProps = {
  id: number;
  subroutineId: number;
  arg: Arg;
  binding: string;
  // Errors about the arg itself, computed by the owning subroutine's view via the central validators.
  typeErrors: ValidationError[];
  data?: Arg[];
  index?: number;
  onArrowClick?: (data: Arg[], arrowType: string) => void;
  onChange: (newArg: Arg, newBinding: string) => void;
  onDelete?: () => void;
  type?: string;
  disableFields?: boolean;
};

const ArgView: FC<ArgViewProps> = (props) => {
  const { application } = useApplication();
  const { metadata } = useMetadata();

  const [openVars, setOpenVars] = useState(false);

  const variableScopes = metadata.scopelists?.find((list) => list.name === 'arguments')?.scopes;
  const scope = getVariableScope(variableScopes, props.binding, props.subroutineId, application, metadata);

  const bindingType = scope
    ? getChipType({ name: props.binding, scope: scope }, application, metadata, props.subroutineId)
    : undefined;
  const bindingTypeLabel = metadata.datatypes.find((datatype) => datatype.type === bindingType)?.label;

  const metadataTypes = metadata.typelists?.find((typelist) => typelist.name === 'argument')?.types || [];
  // Event args get their type from the metadata; every other arg declares its own.
  const type = props.type || props.arg?.type;
  const pickerType = type ? [type] : metadataTypes;

  const handleNewVariablePick = (newVariable: VariableRef) => {
    const pickedType = getChipType(newVariable, application, metadata, props.subroutineId);
    props.onChange({ ...props.arg, type: props.arg.type || pickedType || '' }, newVariable.name);
  };

  const getErrors = () => {
    const errors: ValidationError[] = [];

    const node = application?.blocks.nodes[props.id];
    const blockName = `${props.id}: ${node.type}${node.label && ` (${node.label})`}`;

    if (
      props.binding &&
      (!scope ||
        !variableExists(
          application.variables,
          application.subroutines,
          metadata,
          { name: props.binding, scope: scope },
          props.subroutineId
        ))
    ) {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} is referring to a variable that doesn't exist`,
        id: props.id
      });
    }

    return [...errors, ...props.typeErrors];
  };

  const errors = getErrors();

  return (
    <ErrorNodeWrapper key={props.arg.id} errors={errors}>
      <ArgChip
        value={props.arg}
        binding={props.binding}
        type={type}
        variableLabel={bindingTypeLabel}
        data={props.data}
        index={props.index}
        onArrowClick={props.onArrowClick}
        onClick={() => setOpenVars(true)}
        onChipDelete={() => props.onChange(props.arg, '')}
        onDelete={props.onDelete}
        handleChange={(label) => props.onChange({ ...props.arg, label: label }, props.binding)}
        onTypeChange={(newType) =>
          props.onChange({ ...props.arg, type: newType }, bindingType === newType ? props.binding : '')
        }
        scope={scope}
        error={errors[0]?.severity}
        disableFields={props.disableFields}
      />
      <VariablePicker
        subroutineId={props.subroutineId}
        open={openVars}
        onClose={() => setOpenVars(false)}
        type={pickerType}
        onVariablePicked={handleNewVariablePick}
        scope={variableScopes}
      />
    </ErrorNodeWrapper>
  );
};

export default ArgView;
